import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLSelectElement} [planSelect] - Classic skin's selling plan dropdown (when product has plans)
 * @property {HTMLInputElement} [quantityInput] - Hidden quantity input bound to the sticky form
 * @property {HTMLSelectElement} [compactSelect] - Compact skin's dropdown (only when the purchase option is selectable)
 * @property {HTMLElement} [compactPlanName] - Compact skin: plan name on line one
 * @property {HTMLElement} [compactPlanPercent] - Compact skin: saving on line one
 * @property {HTMLElement} [compactPlanPrice] - Compact skin: per-bag price on line two
 * @property {HTMLElement} [compactPlanGift] - Compact skin: "+ free gift", shown for subscriptions only
 */

/**
 * @typedef {Object} StickyPlanPricing
 * @property {string} 1 - Formatted per-bag price for one bag
 * @property {string} [2] - Formatted per-bag price for two bags
 * @property {{1: number, 2?: number}} [percent] - Saving against the one-time price, per bag quantity
 */

/**
 * @typedef {Object} StickyPricingData
 * @property {string} onetime - Formatted one-time purchase price
 * @property {Object<string, StickyPlanPricing>} plans - Per-plan, per-bag-quantity prices
 */

/**
 * The theme's mobile breakpoint. The compact skin's stylesheet uses the same query to
 * decide which skin is visible, so the two must stay in step.
 */
const MOBILE_QUERY = '(max-width: 749px)';

/**
 * A custom element that manages a sticky add-to-cart bar.
 *
 * The bar follows one of two visibility policies (see #visibilityPolicy):
 * - after-scroll: it shows once the main buy buttons have scrolled up out of view.
 * - while-buy-buttons-off-screen: it shows whenever the main buy buttons are off screen,
 *   above or below, so it is up from page load (the compact skin on mobile).
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {AbortController | null} */
  #childController = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {'while-buy-buttons-off-screen' | 'after-scroll' | null} */
  #appliedPolicy = null;

  /** @type {StickyPricingData | null} */
  #priceData = null;

  /** @type {HTMLInputElement[]} */
  #qtyRadios = [];

  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia(MOBILE_QUERY);

  connectedCallback() {
    super.connectedCallback();

    // disconnectedCallback aborts the controller, and nothing else replaces it. Without
    // this, an element that is moved rather than replaced reconnects with an aborted
    // signal, and the listeners below are never registered: the breakpoint would stop
    // switching skins, and the cart safety net would stop firing.
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#applyVisibilityPolicy();
    this.#mobileQuery.addEventListener('change', () => this.#applyVisibilityPolicy(), {
      signal: this.#abortController.signal,
    });
    this.#setupPriceSync();
    this.#ensureFormBindings();
    this.#wireCartAddSafetyNet();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownScrollObservers();
    this.#abortController.abort();
    this.#childController?.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Fires after morph rewrites this element in place — e.g. when
   * `custom-variant-picker.js` swaps to a sibling product. Morph leaves the
   * <option> labels at their server baseline (plan name only) and replaces the
   * <script type="application/json"> pricing payload, so the cached #priceData
   * is stale. Re-parse from the live DOM and re-decorate the dropdowns.
   *
   * Relies on `morph.js` passing the live (preserved) node to onAfterUpdate so
   * `instanceof Component` matches and this fires.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyVisibilityPolicy();
    this.#setupPriceSync();
    this.#ensureFormBindings();
  }

  /**
   * Handles a change on any skin's purchase-option dropdown.
   *
   * The hidden `selling_plan` input is the single source of truth: it is what the
   * form submits. Every other dropdown is set to match it, so a phone rotated past
   * the mobile breakpoint (which swaps skins) shows the same choice in both.
   *
   * @param {Event} event
   */
  onChangeSellingPlan(event) {
    const hiddenInput = this.#sellingPlanInput();
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!hiddenInput) return;

    // An empty selling_plan is how Shopify recognises a one-time purchase.
    hiddenInput.value = select.value === 'onetime' ? '' : select.value;

    for (const other of this.#planSelects()) {
      if (other !== select) other.value = select.value;
    }

    this.#syncPrice();
  }

  /**
   * Reads pricing JSON emitted by the `subscription-pricing-data` snippet, then
   * mirrors the main form's bag-quantity radio so the sticky bar's option labels
   * and submitted quantity reflect the user's current selection.
   */
  #setupPriceSync() {
    const dataId = this.dataset.priceDataId;
    if (!dataId) return;

    const scriptEl = document.getElementById(dataId);
    if (!scriptEl) return;

    try {
      this.#priceData = /** @type {StickyPricingData} */ (JSON.parse(scriptEl.textContent ?? '{}'));
    } catch {
      return;
    }

    // Bag-quantity radios live in the main product form's custom-subscription block.
    // Scope to the same shopify section to handle multiple product widgets per page.
    const sectionElement = this.closest('.shopify-section');
    const root = sectionElement ?? document;
    this.#qtyRadios = Array.from(
      root.querySelectorAll('custom-subscription input[name="quantity"]')
    );

    // Fresh child controller drops listeners from any prior call so re-runs
    // (via updatedCallback after a morph) don't pile up duplicates. The host-
    // level #abortController stays alive so the CartAddEvent listener persists.
    this.#childController?.abort();
    this.#childController = new AbortController();
    const { signal } = this.#childController;

    for (const radio of this.#qtyRadios) {
      radio.addEventListener('change', () => this.#syncPrice(), { signal });
    }

    this.#syncPrice();
  }

  /**
   * @returns {string} Currently selected bag quantity ("1" or "2"), defaulting to "1".
   */
  #getCurrentQty() {
    for (const radio of this.#qtyRadios) {
      if (radio.checked) return radio.value;
    }
    return '1';
  }

  /**
   * @returns {HTMLInputElement | null} The hidden `selling_plan` input the form submits.
   */
  #sellingPlanInput() {
    return this.querySelector('input.selling_plan_id');
  }

  /**
   * @returns {HTMLSelectElement[]} Every skin's purchase-option dropdown present in the DOM.
   */
  #planSelects() {
    const { planSelect, compactSelect } = this.refs;
    return [planSelect, compactSelect].filter((select) => select instanceof HTMLSelectElement);
  }

  /**
   * Updates each <option>'s label with the discounted price for the current
   * bag quantity, mirrors the quantity into the sticky form's hidden input so
   * submission matches what the user configured up the page, and rewrites the
   * compact skin's summary to describe what the button will add.
   */
  #syncPrice() {
    if (!this.#priceData) return;

    const qty = this.#getCurrentQty();
    const { quantityInput } = this.refs;

    for (const select of this.#planSelects()) {
      for (const option of Array.from(select.options)) {
        const baseLabel = option.dataset.baseLabel ?? option.textContent ?? '';
        const priceLabel = this.#priceFor(option.value, qty);

        option.textContent = priceLabel
          ? `${baseLabel}    ${priceLabel}`
          : baseLabel;
      }
    }

    if (quantityInput) {
      quantityInput.value = qty;
    }

    this.#syncCompactSummary(qty);
  }

  /**
   * @param {string} planId - A selling plan id, or "onetime"
   * @param {string} qty - Bag quantity, "1" or "2"
   * @returns {string} The formatted per-bag price, or "" when unknown
   */
  #priceFor(planId, qty) {
    if (!this.#priceData) return '';
    if (planId === 'onetime') return this.#priceData.onetime ?? '';

    const planPrices = this.#priceData.plans?.[planId];
    // Fall back to qty=1 if the requested qty isn't in the dataset.
    return planPrices?.[qty] ?? planPrices?.['1'] ?? '';
  }

  /**
   * @param {string} planId - A selling plan id, or "onetime"
   * @param {string} qty - Bag quantity, "1" or "2"
   * @returns {number} Saving against the one-time price in percent, 0 when none
   */
  #percentFor(planId, qty) {
    if (planId === 'onetime') return 0;

    const percent = this.#priceData?.plans?.[planId]?.percent;
    return Number(percent?.[qty] ?? percent?.['1'] ?? 0);
  }

  /**
   * Writes the compact skin's two lines for the purchase option the form will submit.
   * Does nothing when the compact skin is not rendered.
   *
   * @param {string} qty - Bag quantity, "1" or "2"
   */
  #syncCompactSummary(qty) {
    const { compactPlanName, compactPlanPercent, compactPlanPrice, compactPlanGift, compactSelect } = this.refs;
    if (!compactPlanName || !compactPlanPercent || !compactPlanPrice) return;

    const planId = this.#sellingPlanInput()?.value || 'onetime';
    // Without a dropdown the submitted plan never changes, so the server-rendered name stands.
    const option = compactSelect
      ? Array.from(compactSelect.options).find((candidate) => candidate.value === planId)
      : null;
    const name = option?.dataset.baseLabel ?? compactPlanName.dataset.baseLabel ?? compactPlanName.textContent ?? '';
    const percent = this.#percentFor(planId, qty);
    const price = this.#priceFor(planId, qty);

    compactPlanName.textContent = name;
    compactPlanPercent.textContent = `${percent}%`;
    compactPlanPercent.hidden = percent <= 0;
    if (price) compactPlanPrice.textContent = price;
    // The gift comes with the subscription, so it goes away on a one-time purchase.
    if (compactPlanGift) compactPlanGift.hidden = planId === 'onetime';
  }

  /**
   * Which visibility policy applies right now.
   *
   * - 'while-buy-buttons-off-screen': the compact skin on mobile. On a phone the buy
   *   buttons start below the fold, so the bar is up from page load, and it steps aside
   *   while they are on screen so the page never shows two Add to Cart buttons at once.
   *   Its CSS shows the bar before any script runs and keeps room for it on the page and
   *   around the chat launcher, even while it is stepped aside.
   * - 'after-scroll': every other case. The bar slides in once the main buy buttons
   *   have scrolled up out of view.
   *
   * To change when a viewport shows the bar, change this method. The CSS that picks
   * the skin per viewport lives in the skin snippets.
   *
   * @returns {'while-buy-buttons-off-screen' | 'after-scroll'}
   */
  #visibilityPolicy() {
    return this.dataset.mode === 'compact' && this.#mobileQuery.matches
      ? 'while-buy-buttons-off-screen'
      : 'after-scroll';
  }

  /**
   * Puts the bar under the current policy. Safe to call repeatedly: on connect, after
   * a morph, and whenever the viewport crosses the mobile breakpoint.
   */
  #applyVisibilityPolicy() {
    const policy = this.#visibilityPolicy();

    // Already watching the buy buttons under this policy; a morph keeps the observed nodes
    // alive. It does reset data-stuck to the server markup, so put back the last state.
    if (policy === this.#appliedPolicy && this.#buyButtonsIntersectionObserver) {
      if (this.#isStuck) {
        this.#showStickyBar();
      } else {
        this.#hideStickyBar();
      }
      return;
    }

    this.#teardownScrollObservers();
    this.#appliedPolicy = policy;

    if (policy === 'while-buy-buttons-off-screen') {
      // Up until the observer reports the buy buttons on screen. If they can't be found,
      // the bar simply stays up.
      this.#showStickyBar();
      this.#watchBuyButtonsOnScreen();
      return;
    }

    this.#hideStickyBar();
    this.#setupIntersectionObserver();
  }

  /**
   * Shows the bar while the main buy buttons are off screen, above or below, and hides it
   * while any part of them is on screen. It hides as soon as they reach the edge of the
   * screen, where they are still behind the bar, so the two buttons never overlap.
   */
  #watchBuyButtonsOnScreen() {
    const buyButtonsBlock = this.#getProductForm()?.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (entry.isIntersecting) {
        this.#hideStickyBar();
      } else {
        this.#showStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;
    const fixedParallaxSection = document.querySelector('.footer-parallax-target');
    const isFixedFooter = fixedParallaxSection
      ? getComputedStyle(fixedParallaxSection).position === 'fixed'
      : false;
    const mainBottomTarget = isFixedFooter
      ? document.querySelector('.main-content-end-sentinel')
      : footer;
    if (!mainBottomTarget) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for main content bottom visibility - hides sticky bar at page bottom
    const bottomObserverRootMargin = isFixedFooter ? '0px 0px 200px 0px' : '200px 0px 0px 0px';
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Bottom boundary out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: bottomObserverRootMargin,
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(mainBottomTarget);
  }

  /**
   * Stops the after-scroll observers, leaving them ready to be set up again.
   */
  #teardownScrollObservers() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;
    this.#mainBottomObserver = null;
    this.#hiddenByBottom = false;
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Defensive: ensures the hidden inputs that bind to the buy-buttons form
   * via form="sticky-atc-form" are still present and correctly anchored.
   * If iOS Safari ever strips the form attribute during DOM mutations,
   * we restore it.
   */
  #ensureFormBindings() {
    const inputs = this.querySelectorAll('input.selling_plan_id, input.sticky-quantity-input');
    for (const input of inputs) {
      if (input.getAttribute('form') !== 'sticky-atc-form') {
        input.setAttribute('form', 'sticky-atc-form');
      }
    }
  }

  /**
   * Defensive: after a successful CartAddEvent, ensure our buttons are
   * re-enabled and not stuck in loading state. The product-form-component
   * already does this via setLoading(false), but iOS Safari has shown
   * intermittent failures where the second submit silently no-ops. This
   * safety net forces the sticky bar's add-to-cart-component back to a
   * clean clickable state.
   */
  #wireCartAddSafetyNet() {
    document.addEventListener(
      CartAddEvent.eventName,
      () => {
        // Defer one microtask so any synchronous follow-up listeners
        // dispatched in the same task (e.g. cart-drawer auto-open)
        // settle before we re-anchor and re-enable.
        queueMicrotask(() => {
          this.#ensureFormBindings();
          for (const container of this.querySelectorAll('add-to-cart-component')) {
            container.setLoading?.(false);
            container.enable?.();
          }
        });
      },
      { signal: this.#abortController.signal }
    );
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} .product-information product-form-component[data-product-id="${productId}"]`
    );
  }


}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
